import React, { useRef, useState, useEffect, useCallback } from "react";

const COLORS = ["#1a1a1a","#9ca3af","#ffffff","#06b6d4","#22c55e","#a855f7","#f97316","#ef4444"];
const SIZES  = [3, 6, 10, 16];
const ASPECT_PRESETS = [
  { label: "Free", ratio: null },
  { label: "1:1",  ratio: 1 },
  { label: "4:3",  ratio: 4 / 3 },
  { label: "16:9", ratio: 16 / 9 },
  { label: "3:4",  ratio: 3 / 4 },
];

export default function ImageEditor({
  filePreviews, setFilePreviews,
  caption, setCaption,
  onSend, onClose, fileInputRef,
}) {
  const [activeIdx,  setActiveIdx]  = useState(0);
  const [activeTool, setActiveTool] = useState("draw");
  const [color,      setColor]      = useState("#ef4444");
  const [brushSize,  setBrushSize]  = useState(1);
  const [drawing,    setDrawing]    = useState(false);
  const [history,    setHistory]    = useState({});

  const [cropBox,    setCropBox]    = useState(null);
  const [cropAspect, setCropAspect] = useState(null);
  const [cropDrag,   setCropDrag]   = useState(null);

  const canvasRef   = useRef(null);
  const imgRef      = useRef(null);
  const ctxRef      = useRef(null);
  const lastPos     = useRef(null);
  const addInputRef = useRef(null);
  const historyRef  = useRef({});

  const active  = filePreviews[activeIdx];
  const isImage = active?.type?.startsWith("image");

  useEffect(() => { historyRef.current = history; }, [history]);

  const replay = (ctx, strokes) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    (strokes || []).forEach(s => {
      ctx.strokeStyle = s.color; ctx.lineWidth = s.size;
      ctx.lineCap = "round"; ctx.lineJoin = "round";
      ctx.beginPath();
      s.points.forEach(([x, y], i) => i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y));
      ctx.stroke();
    });
  };

  useEffect(() => {
    const img = imgRef.current, canvas = canvasRef.current;
    if (!img || !canvas || !isImage) return;
    const setup = () => {
      canvas.width  = img.naturalWidth  || img.offsetWidth;
      canvas.height = img.naturalHeight || img.offsetHeight;
      const ctx = canvas.getContext("2d");
      ctx.lineCap = "round"; ctx.lineJoin = "round";
      ctxRef.current = ctx;
      replay(ctx, historyRef.current[activeIdx] || []);
    };
    if (img.complete) setup(); else img.onload = setup;
    return () => { if (img) img.onload = null; };
  }, [activeIdx, filePreviews, isImage]);

  useEffect(() => {
    if (activeTool !== "crop") { setCropBox(null); return; }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const p = 0.1;
    setCropBox({ x: canvas.width*p, y: canvas.height*p, w: canvas.width*(1-p*2), h: canvas.height*(1-p*2) });
  }, [activeTool, activeIdx]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const src    = e.touches ? e.touches[0] : e;
    return [
      (src.clientX - rect.left) * (canvas.width  / rect.width),
      (src.clientY - rect.top)  * (canvas.height / rect.height),
    ];
  };

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  const hitHandle = (x, y, box) => {
    const { x: bx, y: by, w: bw, h: bh } = box;
    const E = 18;
    const pts = [
      {id:"tl",cx:bx,cy:by}, {id:"tr",cx:bx+bw,cy:by},
      {id:"bl",cx:bx,cy:by+bh}, {id:"br",cx:bx+bw,cy:by+bh},
      {id:"tm",cx:bx+bw/2,cy:by}, {id:"bm",cx:bx+bw/2,cy:by+bh},
      {id:"ml",cx:bx,cy:by+bh/2}, {id:"mr",cx:bx+bw,cy:by+bh/2},
    ];
    for (const p of pts) if (Math.abs(x-p.cx)<E && Math.abs(y-p.cy)<E) return p.id;
    if (x>bx+E && x<bx+bw-E && y>by+E && y<by+bh-E) return "move";
    return null;
  };

  const onCropDown = (e) => {
    e.preventDefault();
    const [x,y] = getPos(e);
    if (!cropBox) return;
    const handle = hitHandle(x,y,cropBox);
    if (!handle) return;
    setCropDrag({ handle, startX:x, startY:y, origBox:{...cropBox} });
  };

  const onCropMove = (e) => {
    e.preventDefault();
    if (!cropDrag) return;
    const canvas = canvasRef.current;
    const cw = canvas.width, ch = canvas.height;
    const [x,y] = getPos(e);
    const dx = x-cropDrag.startX, dy = y-cropDrag.startY;
    let { x:ox, y:oy, w:ow, h:oh } = cropDrag.origBox;
    let nx=ox, ny=oy, nw=ow, nh=oh;
    const h = cropDrag.handle;
    if (h==="move") {
      nx = clamp(ox+dx, 0, cw-ow); ny = clamp(oy+dy, 0, ch-oh);
    } else {
      if (h.includes("l")||h==="ml") { const r=clamp(ox+dx,0,ox+ow-20); nw=ow-(r-ox); nx=r; }
      if (h.includes("r")||h==="mr") { nw=clamp(ow+dx,20,cw-ox); }
      if (h.includes("t")||h==="tm") { const r=clamp(oy+dy,0,oy+oh-20); nh=oh-(r-oy); ny=r; }
      if (h.includes("b")||h==="bm") { nh=clamp(oh+dy,20,ch-oy); }
      if (cropAspect) {
        if (h.includes("l")||h.includes("r")||h==="ml"||h==="mr") { nh=nw/cropAspect; if(h.includes("t")) ny=oy+oh-nh; }
        else { nw=nh*cropAspect; if(h.includes("l")) nx=ox+ow-nw; }
      }
    }
    setCropBox({ x:nx, y:ny, w:nw, h:nh });
  };

  const onCropUp = (e) => { e.preventDefault(); setCropDrag(null); };

  const applyCrop = useCallback(async () => {
    if (!cropBox || !imgRef.current) return;
    const img = imgRef.current, canvas = canvasRef.current;
    const scX = img.naturalWidth/canvas.width, scY = img.naturalHeight/canvas.height;
    const out = document.createElement("canvas");
    out.width  = Math.round(cropBox.w*scX);
    out.height = Math.round(cropBox.h*scY);
    const ctx = out.getContext("2d");
    ctx.drawImage(img, cropBox.x*scX, cropBox.y*scY, cropBox.w*scX, cropBox.h*scY, 0, 0, out.width, out.height);
    if (canvasRef.current) ctx.drawImage(canvasRef.current, cropBox.x, cropBox.y, cropBox.w, cropBox.h, 0, 0, out.width, out.height);
    const blob    = await new Promise(r => out.toBlob(r, active.file?.type||"image/png"));
    const newFile = new File([blob], active.file?.name||"cropped.png", { type:blob.type });
    setFilePreviews(prev => { const n=[...prev]; n[activeIdx]={ file:newFile, url:URL.createObjectURL(newFile), type:newFile.type }; return n; });
    setHistory(prev => { const n={...prev,[activeIdx]:[]}; historyRef.current=n; return n; });
    setActiveTool("draw");
  }, [cropBox, activeIdx, active]);

  const onDrawStart = (e) => {
    e.preventDefault(); setDrawing(true);
    const pos = getPos(e); lastPos.current = pos;
    setHistory(prev => {
      const next={...prev,[activeIdx]:[...(prev[activeIdx]||[]),{color,size:SIZES[brushSize],points:[pos]}]};
      historyRef.current=next; return next;
    });
  };

  const onDrawMove = (e) => {
    e.preventDefault(); if (!drawing) return;
    const pos=getPos(e), ctx=ctxRef.current; if (!ctx) return;
    ctx.strokeStyle=color; ctx.lineWidth=SIZES[brushSize]; ctx.lineCap="round"; ctx.lineJoin="round";
    ctx.beginPath(); ctx.moveTo(...lastPos.current); ctx.lineTo(...pos); ctx.stroke();
    lastPos.current=pos;
    setHistory(prev => {
      const strokes=[...(prev[activeIdx]||[])];
      const last={...strokes[strokes.length-1],points:[...strokes[strokes.length-1].points,pos]};
      strokes[strokes.length-1]=last;
      const next={...prev,[activeIdx]:strokes}; historyRef.current=next; return next;
    });
  };

  const onDrawEnd = (e) => { e.preventDefault(); setDrawing(false); };

  const onCanvasDown = (e) => activeTool==="crop" ? onCropDown(e) : onDrawStart(e);
  const onCanvasMove = (e) => activeTool==="crop" ? onCropMove(e) : onDrawMove(e);
  const onCanvasUp   = (e) => activeTool==="crop" ? onCropUp(e)   : onDrawEnd(e);

  const handleUndo = () => {
    setHistory(prev => {
      const strokes=[...(prev[activeIdx]||[])]; strokes.pop();
      const next={...prev,[activeIdx]:strokes}; historyRef.current=next;
      if (ctxRef.current) replay(ctxRef.current,strokes); return next;
    });
  };

  const handleAdd = (e) => {
    const files=Array.from(e.target.files); if (!files.length) return; e.target.value="";
    setFilePreviews(prev=>[...prev,...files.map(f=>({file:f,url:URL.createObjectURL(f),type:f.type}))]);
  };

  const exportMarkedImage = async () => {
    const img=imgRef.current, out=document.createElement("canvas");
    out.width=img.naturalWidth||img.offsetWidth; out.height=img.naturalHeight||img.offsetHeight;
    const ctx=out.getContext("2d");
    ctx.drawImage(img,0,0,out.width,out.height);
    if (canvasRef.current) ctx.drawImage(canvasRef.current,0,0,out.width,out.height);
    return new Promise(r=>out.toBlob(blob=>r(new File([blob],active.file?.name||"image.png",{type:blob.type})),active.file?.type||"image/png"));
  };

  const handleSend = async () => {
    const results = await Promise.all(filePreviews.map(async (item,idx)=>{
      const isImg=item.type?.startsWith("image"), hasMarks=!!(historyRef.current[idx]?.length);
      let markedFile=null;
      if (isImg&&hasMarks) { if(idx!==activeIdx){setActiveIdx(idx);await new Promise(r=>setTimeout(r,60));} markedFile=await exportMarkedImage(); }
      return {idx,markedFile,hasMarks,originalFile:item.file,caption};
    }));
    onSend(results);
  };

  if (!active) return null;

  const CropOverlay = () => {
    if (!cropBox||!canvasRef.current) return null;
    const canvas=canvasRef.current;
    const {x,y,w,h}=cropBox;
    const el=canvas.getBoundingClientRect();
    const scX=el.width/canvas.width, scY=el.height/canvas.height;
    const dx=x*scX, dy=y*scY, dw=w*scX, dh=h*scY;
    const handles=[
      {id:"tl",cx:dx,cy:dy},{id:"tr",cx:dx+dw,cy:dy},
      {id:"bl",cx:dx,cy:dy+dh},{id:"br",cx:dx+dw,cy:dy+dh},
      {id:"tm",cx:dx+dw/2,cy:dy},{id:"bm",cx:dx+dw/2,cy:dy+dh},
      {id:"ml",cx:dx,cy:dy+dh/2},{id:"mr",cx:dx+dw,cy:dy+dh/2},
    ];
    return (
      <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none",overflow:"visible"}}>
        <defs>
          <mask id="cm">
            <rect x="0" y="0" width="100%" height="100%" fill="white"/>
            <rect x={dx} y={dy} width={dw} height={dh} fill="black"/>
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.52)" mask="url(#cm)"/>
        <rect x={dx} y={dy} width={dw} height={dh} fill="none" stroke="white" strokeWidth="1.5"/>
        {[dw/3,dw*2/3].map((rx,i)=><line key={i} x1={dx+rx} y1={dy} x2={dx+rx} y2={dy+dh} stroke="rgba(255,255,255,0.3)" strokeWidth="0.8"/>)}
        {[dh/3,dh*2/3].map((ry,i)=><line key={i} x1={dx} y1={dy+ry} x2={dx+dw} y2={dy+ry} stroke="rgba(255,255,255,0.3)" strokeWidth="0.8"/>)}
        {handles.map(hh=><circle key={hh.id} cx={hh.cx} cy={hh.cy} r="7" fill="white" stroke="rgba(0,0,0,0.35)" strokeWidth="1.5"/>)}
      </svg>
    );
  };

  return (
    <div style={{display:"flex",flexDirection:"column",background:"#0d1117",flex:1,minHeight:0,width:"100%",overflow:"hidden"}}>

      {/* Top bar */}
   {/* Top bar */}
<div style={{
  borderBottom: "1px solid #1e293b",
  flexShrink: 0,
  zIndex: 10,
  position: "relative",
  background: "#0d1117",
}}>
  <div className="settopme" style={{
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    overflowX: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    WebkitOverflowScrolling: "touch",
    whiteSpace: "nowrap",   // ← prevents wrapping
  }}>
    <ToolBtn active={activeTool === "draw"} onClick={() => setActiveTool("draw")}>✏️ Draw</ToolBtn>
    <ToolBtn active={activeTool === "crop"} onClick={() => setActiveTool("crop")}>✂️ Crop</ToolBtn>

    {activeTool === "draw" && (
      <button onClick={handleUndo} style={topBtn}>↩ Undo</button>
    )}

    {activeTool === "crop" && (
      <>
        {ASPECT_PRESETS.map(p => (
          <button key={p.label} onClick={() => {
            setCropAspect(p.ratio);
            if (p.ratio && cropBox) setCropBox(b => ({ ...b, h: b.w / p.ratio }));
          }} style={{
            ...topBtn,
            background: cropAspect === p.ratio ? "#25d366" : "transparent",
            color: cropAspect === p.ratio ? "#fff" : "#94a3b8",
            borderColor: cropAspect === p.ratio ? "#25d366" : "#1e293b",
            padding: "3px 8px",
            fontSize: 11,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}>
            {p.label}
          </button>
        ))}
        <button onClick={applyCrop} style={{
          ...topBtn,
          background: "#25d366",
          color: "#fff",
          borderColor: "#25d366",
          padding: "4px 12px",
          fontWeight: 600,
          flexShrink: 0,
        }}>
          ✓ Apply
        </button>
      </>
    )}

    {/* Spacer */}
    <span style={{ color: "#64748b", fontSize: 12, marginLeft: "auto", flexShrink: 0 }}>
      {activeIdx + 1}/{filePreviews.length}
    </span>
    <button onClick={onClose} style={{ ...topBtn, fontSize: 16, flexShrink: 0 }}>✕</button>
  </div>
</div>

      {/* Image area */}
      <div style={{flex:1,minHeight:0,position:"relative",overflow:"hidden"}}>
        {isImage ? (
          <div style={{position:"absolute",inset:"10px 12px",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{position:"relative",display:"inline-flex",maxWidth:"100%",maxHeight:"100%"}}>
              <img ref={imgRef} src={active.url} draggable={false} alt="edit"
                style={{maxWidth:"100%",maxHeight:"100%",width:"auto",height:"auto",objectFit:"contain",borderRadius:10,display:"block",userSelect:"none",pointerEvents:"none"}}/>
              <canvas ref={canvasRef}
                onMouseDown={onCanvasDown} onMouseMove={onCanvasMove} onMouseUp={onCanvasUp} onMouseLeave={onCanvasUp}
                onTouchStart={onCanvasDown} onTouchMove={onCanvasMove} onTouchEnd={onCanvasUp}
                style={{position:"absolute",inset:0,width:"100%",height:"100%",borderRadius:10,touchAction:"none",
                  cursor:activeTool==="crop"?(cropDrag?"grabbing":"crosshair"):"crosshair"}}/>
              {activeTool==="crop" && <CropOverlay/>}
            </div>
          </div>
        ) : (
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#94a3b8",fontSize:13}}>
            📄 {active.file?.name}
          </div>
        )}
      </div>

      {/* Draw controls */}
      {activeTool==="draw" && (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 16px",borderTop:"1px solid #1e293b",flexShrink:0}}>
          <div style={{display:"flex",gap:7,alignItems:"center"}}>
            {COLORS.map(c=>(
              <div key={c} onClick={()=>setColor(c)} style={{width:color===c?26:20,height:color===c?26:20,borderRadius:"50%",background:c,cursor:"pointer",border:color===c?"3px solid #fff":"2px solid #334155",transition:"all .15s",flexShrink:0}}/>
            ))}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {SIZES.map((s,i)=>(
              <div key={i} onClick={()=>setBrushSize(i)} style={{width:s+6,height:s+6,borderRadius:"50%",background:brushSize===i?"#fff":"#475569",cursor:"pointer",transition:"all .15s",border:brushSize===i?"2px solid #25d366":"none"}}/>
            ))}
          </div>
        </div>
      )}

      {/* Thumbnails */}
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",overflowX:"auto",flexShrink:0}}>
        {filePreviews.map((item,i)=>(
          <div key={i} style={{width:56,height:56,borderRadius:8,overflow:"hidden",flexShrink:0,border:activeIdx===i?"2.5px solid #25d366":"2.5px solid transparent",cursor:"pointer",position:"relative"}}>
            <div onClick={()=>setActiveIdx(i)} style={{width:"100%",height:"100%"}}>
              {item.type?.startsWith("image")
                ?<img src={item.url} alt="thumb" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                :<div style={{width:"100%",height:"100%",background:"#1e293b",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>📄</div>}
            </div>
            <button onClick={()=>{
              setFilePreviews(prev=>{const n=prev.filter((_,idx)=>idx!==i);if(activeIdx>=n.length)setActiveIdx(Math.max(0,n.length-1));return n;});
              setHistory(prev=>{const n={};Object.keys(prev).forEach(idx=>{const ni=Number(idx);if(ni<i)n[ni]=prev[ni];else if(ni>i)n[ni-1]=prev[ni];});historyRef.current=n;return n;});
            }} style={{position:"absolute",top:2,right:2,width:18,height:18,borderRadius:"50%",background:"#ef4444",color:"#fff",border:"none",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",zIndex:2}}>✕</button>
          </div>
        ))}
        <div onClick={()=>addInputRef.current?.click()} style={{width:56,height:56,borderRadius:8,flexShrink:0,border:"2px dashed #334155",background:"#1e293b",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"#64748b",fontSize:26}}>+</div>
        <input ref={addInputRef} type="file" hidden multiple accept="image/*,video/*,*/*" onChange={handleAdd} onClick={e=>{e.target.value="";}}/>
      </div>

      {/* Caption + Send */}
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px 12px",borderTop:"1px solid #1e293b",flexShrink:0,background:"#0d1117"}}>
        <span onClick={()=>addInputRef.current?.click()} style={{fontSize:22,cursor:"pointer",color:"#64748b",lineHeight:1}}>+</span>
        <input value={caption} onChange={e=>setCaption(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSend()} placeholder="Add a caption..."
          style={{flex:1,background:"transparent",border:"none",outline:"none",color:"#f1f5f9",fontSize:14}}/>
        <button onClick={handleSend} style={{width:42,height:42,borderRadius:"50%",background:"#25d366",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="white"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z"/></svg>
        </button>
      </div>
    </div>
  );
}

function ToolBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      background: active ? "#1e293b" : "transparent",
      border: active ? "1px solid #25d366" : "1px solid #1e293b",
      color: active ? "#25d366" : "#64748b",
      borderRadius: 6,
      padding: "4px 10px",
      fontSize: 12,
      cursor: "pointer",
      fontWeight: active ? 600 : 400,
      transition: "all .15s",
      flexShrink: 0,        // ← ADD THIS
      whiteSpace: "nowrap", // ← ADD THIS
    }}>
      {children}
    </button>
  );
}

const topBtn={background:"transparent",border:"1px solid #1e293b",color:"#94a3b8",borderRadius:6,padding:"4px 10px",fontSize:12,cursor:"pointer"};
